import clsx from 'clsx';
import { useEditorRef } from 'platejs/react';
import { useSelectionFragmentProp } from '@platejs/utils/react';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import { setAlign } from '@platejs/basic-styles';
import toolbarStyles from '../PlateToolbar/PlateToolbar.module.scss';

type Alignment = 'left' | 'center' | 'right' | 'justify';

// Use a generic component type for `Icon` instead of `typeof FormatAlignLeftIcon`.
// The latter trips Babel's preset-typescript: the import is used as a value
// (in the OPTIONS array) AND a type annotation, and Babel can't tell which
// to strip ("Cannot transform the imported binding since it's also used in
// a type annotation"). Same fix as PlateToolbar.tsx.
const OPTIONS: Array<{ value: Alignment; label: string; Icon: React.ComponentType<{ fontSize?: 'small' | 'medium' | 'large' }> }> = [
    { value: 'left', label: 'Align left', Icon: FormatAlignLeftIcon },
    { value: 'center', label: 'Align center', Icon: FormatAlignCenterIcon },
    { value: 'right', label: 'Align right', Icon: FormatAlignRightIcon },
    { value: 'justify', label: 'Justify', Icon: FormatAlignJustifyIcon },
];

// The raw `setAlign(editor, value)` does `setNodes({textAlign}, {match})` with
// no `at`, so it targets `editor.selection` as a Range — and `setNodes`
// internally runs `Editor.unhangRange` on that range. On a cross-cell table
// drag whose focus lands at offset 0 of the LAST selected cell's text (a
// realistic drag endpoint), `unhangRange` treats that as a "hanging" position
// and retracts the focus back to the END of the PREVIOUS cell — silently
// dropping the last cell from the range `setAlign` actually applies to, even
// though `getSelectedCells()` (and the visual highlight) correctly include it.
//
// Fix: when a multi-cell table selection is active, call `setAlign` PER CELL,
// scoped via `at: cellPath` (a Path, not a Range) — `unhangRange` is a no-op
// for non-Range `at`, so each cell's blocks get set independently and the
// retraction can't cross a cell boundary. Reuses the library's own `setAlign`
// (and whatever block-type match it derives) rather than reimplementing it;
// falls back to the unmodified single-cell / non-table `setAlign` call.
export function applyAlignToSelection(editor: any, value: Alignment): void {
    try {
        const tableApi = editor?.api?.table ?? editor?.getApi?.({ key: 'table' })?.table;
        const selected = tableApi?.getSelectedCells?.();
        if (Array.isArray(selected) && selected.length > 1) {
            selected.forEach((cell: any) => {
                const cellPath = editor?.api?.findPath?.(cell);
                if (cellPath) (setAlign as any)(editor, value, { at: cellPath });
            });
            return;
        }
    } catch {
        /* defensive — fall through to the default single-selection path */
    }
    (setAlign as any)(editor, value);
}

export function AlignMenu() {
    const editor = useEditorRef();
    // Library hook: reads the `align` prop across the current selection
    // fragment, returns the value if uniform, otherwise undefined.
    // Replaces the hand-rolled useEditorSelector + block({ block: true }) ladder.
    const current: Alignment | undefined =
        ((useSelectionFragmentProp as any)({
            defaultValue: 'left',
            getProp: (node: any) => node?.align,
        }) as Alignment | undefined) ?? 'left';

    return (
        <>
            {OPTIONS.map(({ value, label, Icon }) => (
                <button
                    key={value}
                    type="button"
                    className={clsx(toolbarStyles.btn, current === value && toolbarStyles.btnActive)}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        applyAlignToSelection(editor, value);
                    }}
                    title={label}
                    aria-label={label}
                    aria-pressed={current === value || undefined}
                >
                    <Icon fontSize="small" />
                </button>
            ))}
        </>
    );
}
